"use client";
import { Languages, LoaderCircle, CircleHelp } from "lucide-react";
import {
  Button,
  Progress,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "ui";
import { LanguageSwitcher, LanguageSwitcherProps } from "./LanguageSwitcher";
import { useTranslation } from "../i18n/client";
import ReactCountryFlag from "react-country-flag";

export interface FormTranslaterProps extends LanguageSwitcherProps {
  onSubmit?: () => void;
  onCancel: () => void;
  submitText: string;
  cancelText?: string;
  isLoading?: boolean;
  type?: "button" | "submit" | "reset" | undefined;
  disabled?: boolean;
}

export const FormTranslater = ({
  onSubmit,
  onCancel,
  submitText,
  languages,
  currentLanguage,
  switchLanguage,
  label,
  cancelText,
  isLoading,
  type = "button",
  disabled,
}: FormTranslaterProps) => {
  const { t } = useTranslation("sajilni2.0");
  return (
    <div className="grid w-full min-w-0 grid-cols-1 gap-3 rounded-lg border bg-white p-3 shadow-lg md:grid-cols-[auto_minmax(0,1fr)_auto] md:items-center md:gap-x-4 md:gap-y-3 md:p-4 lg:grid-cols-[auto_minmax(9rem,0.85fr)_minmax(12rem,1.5fr)_auto] lg:gap-6 lg:divide-x lg:p-6 lg:shadow-xl rtl:lg:divide-x-reverse">
      <div className="flex min-w-0 items-center gap-3 md:max-w-[10rem] lg:max-w-none">
        <div className="hidden size-10 shrink-0 items-center justify-center rounded-full bg-[#f9fafb]/80 lg:flex">
          <div className="flex size-8 items-center justify-center rounded-full bg-[#F2F4F7]">
            <Languages className="size-5 text-[#475467]" />
          </div>
        </div>
        <div className="flex min-w-0 flex-col">
          <div className="text-sm font-semibold text-gray-950 lg:text-xl">
            {t("formTranslater.title")}
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="text-primary flex cursor-help items-center gap-1">
                  <CircleHelp className="size-4 shrink-0" />
                  <span className="text-xs font-semibold">
                    {t("formTranslater.howItWorks")}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                align="start"
                className="max-w-[min(420px,calc(100vw-2rem))]"
              >
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <CircleHelp className="text-primary size-4 shrink-0" />
                    <h2 className="text-xs font-bold text-slate-900">
                      {t("formTranslater.howItWorks")}
                    </h2>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] leading-5 text-slate-500">
                      {t("formTranslater.howItWorksBody")}
                    </p>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      <div className="min-w-0 lg:ps-2">
        <LanguageSwitcher
          languages={languages}
          currentLanguage={currentLanguage}
          switchLanguage={switchLanguage}
          label={label}
        />
      </div>
      <div className="flex min-w-0 flex-col gap-2 md:col-span-3 lg:col-span-1 lg:px-2">
        {languages.map((lang) => {
          const pct = Math.ceil(
            ((lang.filledFields || 0) / (lang.fieldsNumber || 1)) * 100
          );
          return (
            <div key={lang.code} className="flex min-w-0 items-center gap-2">
              <ReactCountryFlag
                countryCode={lang.flagCode}
                svg
                style={{ width: "18px", height: "18px" }}
                title={lang.name}
                className="shrink-0"
              />
              <span className="max-w-[5.5rem] shrink-0 truncate text-sm lg:max-w-[7rem] xl:max-w-none">
                {lang.name}
              </span>
              <Progress
                value={pct}
                className="!h-[3px] !w-auto min-w-[3rem] flex-1"
              />
              <span className="w-10 shrink-0 text-end text-xs text-[#344054] lg:w-auto lg:whitespace-nowrap">
                {pct}%
                <span className="hidden xl:inline">
                  {" "}
                  {t("formTranslater.complete")}
                </span>
              </span>
            </div>
          );
        })}
      </div>
      <div className="grid w-full grid-cols-2 gap-2 md:col-start-3 md:row-start-1 md:flex md:w-auto md:flex-col lg:col-start-4 lg:row-start-1 lg:px-2">
        <Button
          type={type}
          onClick={onSubmit}
          className="!bg-primary w-full text-white"
          disabled={isLoading || disabled}
        >
          {isLoading ? (
            <div className="flex items-center justify-center gap-2">
              <LoaderCircle className="size-5 animate-spin" />
              <span>{submitText}...</span>
            </div>
          ) : (
            submitText
          )}
        </Button>
        <Button onClick={onCancel} className="w-full border" type="button">
          {cancelText || t("Cancel")}
        </Button>
      </div>
    </div>
  );
};
